import { useEffect } from 'react';
import { formatModelCode } from '../utils/modelCategoryRanges';
import { getWholesaleQtyError, WHOLESALE_CATEGORY_LABELS } from '../utils/wholesaleOrderForm';

const WholesaleOrderBucketSheet = ({
  open,
  onClose,
  groupedLines = [],
  itemCount = 0,
  formatMoney,
  onEditLine,
  onRemoveLine,
  grandTotal,
  totalAdvance,
  balance,
  hidePrice = false,
  showOrderTotals = true,
}) => {
  useEffect(() => {
    if (!open) return;

    document.body.classList.add('overflow-hidden');

    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.classList.remove('overflow-hidden');
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-[45] transition-opacity duration-300"
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        className="fixed bottom-0 left-0 right-0 z-50 max-h-[80vh] rounded-t-2xl bg-white shadow-2xl flex flex-col lg:left-64"
        role="dialog"
        aria-modal="true"
        aria-labelledby="bucket-sheet-title"
      >
        <div className="mx-auto mt-3 mb-1 h-1 w-10 rounded-full bg-gray-300 shrink-0" />

        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex justify-between items-center shrink-0 rounded-t-2xl">
          <h2 id="bucket-sheet-title" className="text-base sm:text-lg font-bold text-gray-800">
            <span className="sm:hidden">Bucket ({itemCount})</span>
            <span className="hidden sm:inline">
              Order bucket ({itemCount} {itemCount === 1 ? 'item' : 'items'})
            </span>
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-xl p-1 hover:bg-gray-100 rounded shrink-0"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {showOrderTotals && (
          <div className="px-4 py-2.5 grid grid-cols-3 gap-2 text-center border-b border-gray-100 shrink-0">
            <div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500">Total</div>
              <div className="text-xs sm:text-sm font-semibold text-gray-900">{formatMoney(grandTotal)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500">Advance</div>
              <div className="text-xs sm:text-sm font-semibold text-gray-900">{formatMoney(totalAdvance)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500">Balance</div>
              <div className={`text-xs sm:text-sm font-semibold ${balance === 0 ? 'text-green-700' : 'text-red-700'}`}>
                {formatMoney(balance)}
              </div>
            </div>
          </div>
        )}

        <div className="overflow-y-auto flex-1 min-h-0 pb-[max(1rem,env(safe-area-inset-bottom))]">
          {itemCount === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8 px-4">
              Order bucket is empty — tap a model to add items via the side panel
            </p>
          ) : (
            groupedLines.map(({ category, label, lines }) => (
              <div key={category}>
                <div className="sticky top-0 z-10 px-4 py-1.5 bg-gray-100 border-b border-gray-200 text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  {label || WHOLESALE_CATEGORY_LABELS[category] || category}
                </div>
                <div className="divide-y divide-gray-100">
                  {lines.map((line) => {
                    const qtyError = getWholesaleQtyError(line);
                    return (
                      <div
                        key={line.model}
                        className={`p-3 sm:p-4 bg-white ${qtyError ? 'bg-red-50/50' : ''}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-1.5">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {line.modelName}
                            </div>
                            <div className="text-xs text-gray-500 truncate">
                              {formatModelCode(line.category, line.modelNumber)}
                            </div>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <button
                              type="button"
                              onClick={() => onEditLine(line.model)}
                              className="order-action-btn admin-btn-neutral h-7 px-2 text-xs"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => onRemoveLine(line.model)}
                              className="order-action-btn admin-btn-danger h-7 px-2 text-xs"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                        <p className="text-xs text-gray-700 flex flex-wrap gap-x-1 gap-y-0.5">
                          <span>Qty <strong>{line.qty}</strong></span>
                          {!hidePrice && (
                            <>
                              <span className="text-gray-300">·</span>
                              <span>Unit <strong>{formatMoney(line.unitPrice)}</strong></span>
                              <span className="text-gray-300">·</span>
                              <span>Total <strong>{formatMoney(line.totalValue)}</strong></span>
                              <span className="text-gray-300">·</span>
                              <span>Adv <strong>{formatMoney(line.advance)}</strong></span>
                            </>
                          )}
                        </p>
                        {line.colorRequest?.trim() && (
                          <p className="text-xs text-gray-600 mt-1 truncate" title={line.colorRequest}>
                            Note: {line.colorRequest}
                          </p>
                        )}
                        {qtyError && (
                          <p className="text-xs text-red-600 mt-1">{qtyError}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
};

export default WholesaleOrderBucketSheet;
